import { useCallback, useEffect, useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Box, Chip, CircularProgress, Fade, InputAdornment, TextField, Typography } from '@material-ui/core';
import Search from '@material-ui/icons/Search';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { isNaN, isNil, isNumber } from 'lodash';

import CsvExport from '../CsvExport';
import ColumnRemover from './ColumnRemover';
import { useSetTableSearch, useTableRowBounds, useTableSearch } from '../TableState';
import useIsPrinting from '../../../utilities/PrintUtilities';
import { DATATABLE_ROWSTATUS_CLASS } from '../DataTableConstants';
import { useDataStore } from '../DataStore';
import { useCsvColumns } from '../ColumnContext';
import { useDebounceFn } from '../../../utilities/Hooks';

/* eslint-disable react/prop-types */

const useStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.alignItemsCenter,
    },

    searchContainer: {
        ...theme.mixins.flex.grow(1),

        '& .search-field': {
            padding: 0,
            margin: 0,

            '&, & .MuiInputBase-input': {
                fontSize: theme.typography.body2.fontSize,
            },

            '& .search-field-icon': {
                fontSize: '18px',
                lineHeight: '18px',
            },
        },
    },

    indexContainer: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.justifyContentFlexEnd,
        ...theme.mixins.flex.alignItemsCenter,
        minWidth: 210,
    },
}));

export default function Toolbar(props) {
    const {
        loader,
        sorters,
        filters,
        params,
        showSearch,
        autoFocusSearch,
        stickySearch,
        disableCSVExport,
        disableColumnRemover,
    } = props;
    const classes = useStyles(props);
    const rowBounds = useTableRowBounds();
    const search = useTableSearch();
    const setTableSearch = useSetTableSearch();

    const { total, isLoading, hasLoaded } = useDataStore();
    const [searchValue, setSearchValue] = useState(search || '');

    useEffect(() => {
        setSearchValue(search || '');
    }, [search]);

    const [startIndex, endIndex] = rowBounds || [0, 0];

    const updateFilterValue = useCallback(() => setTableSearch(searchValue), [setTableSearch, searchValue]);
    const debouncedOnChange = useDebounceFn(updateFilterValue, 500);

    const onFilterChanged = useCallback(
        (event) => {
            setSearchValue(event.target.value);
            debouncedOnChange();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [updateFilterValue, setSearchValue]
    );

    const columns = useCsvColumns();
    const start = Math.min(startIndex + 1, total);
    const end = Math.min(endIndex, total);
    const isPrinting = useIsPrinting();

    return (
        <div className={classes.root}>
            {!!stickySearch && <Chip label={stickySearch} size="small" />}

            <div className={classes.searchContainer}>
                {showSearch && (
                    <TextField
                        className="search-field"
                        name="ToolbarSearch"
                        placeholder={isPrinting ? '' : i18n._(t`Search...`)}
                        size="small"
                        fullWidth
                        value={searchValue}
                        InputProps={{
                            autoFocus: autoFocusSearch,
                            disableUnderline: true,
                            onChange: onFilterChanged,
                            startAdornment: !isPrinting && (
                                <InputAdornment position="start">
                                    <Search className="search-field-icon" />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            </div>

            <div className={classes.indexContainer}>
                <Fade in={isLoading && hasLoaded}>
                    <Box mr={1} height={18}>
                        <CircularProgress size={14} />
                    </Box>
                </Fade>

                {hasLoaded && (
                    <Typography variant="body2" component="span" className={DATATABLE_ROWSTATUS_CLASS}>
                        Showing {asInt(start)} - {asInt(end)} of {asInt(total)} results
                    </Typography>
                )}
            </div>
            {!disableCSVExport && (
                <CsvExport columns={columns} loader={loader} sorters={sorters} filters={filters} params={params} />
            )}
            {!disableColumnRemover && <ColumnRemover />}
        </div>
    );
}

function asInt(value, defaultValue = 0) {
    if (isNaN(value) || isNil(value) || !isNumber(value)) return defaultValue;

    return value;
}
